import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { clientCreationApi, type CreationReceipt } from '../../../lib/clientCreationApi';
import type { PlanData } from '../../../lib/clientOnboardingApi';
import type { ClientOnboardingDraft } from '../../../types/clientOnboarding';
import ResumeClientCreation, { receiptStorageKey, savedCreation } from './ResumeClientCreation';

interface Props { draft: ClientOnboardingDraft; plan: PlanData }

function Creation({ draft, plan }: Props) {
  const { t } = useTranslation('admin_client_onboarding');
  const { user } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [capabilityFailed, setCapabilityFailed] = useState(false);
  const [capabilityAttempt, setCapabilityAttempt] = useState(0);
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [done, setDone] = useState(false);
  const current = useRef<CreationReceipt | null>(null);
  const mounted = useRef(true);
  const inFlight = useRef(false);
  const owner = JSON.stringify([user?.id, user?.org_id]);
  const identity = useRef(owner);
  identity.current = owner;
  const storageKey = receiptStorageKey(user!);
  const expectedId = `apply-${plan.plan_digest.slice(7, 31)}`;
  const pendingId = savedCreation(storageKey);
  useEffect(() => {
    mounted.current = true;
    setCapabilityFailed(false);
    clientCreationApi.capabilities().then(data => {
      if (mounted.current) setEnabled(data.apply_enabled === true && draft.farms.length <= data.max_farms);
    }).catch(() => { if (mounted.current) setCapabilityFailed(true); });
    try {
      const saved = sessionStorage.getItem(storageKey);
      if (saved === expectedId) {
        current.current = { apply_id: saved, plan_digest: plan.plan_digest, status: 'UNKNOWN' };
      }
    } catch { /* Receipt remains usable in memory when browser storage is blocked. */ }
    return () => { mounted.current = false; };
  }, [storageKey, plan.plan_digest, draft.farms.length, expectedId, capabilityAttempt]);

  const create = async () => {
    if (!enabled || !accepted || inFlight.current || user?.role !== 'superadmin') return;
    const actor = owner;
    const active = () => mounted.current && identity.current === actor;
    inFlight.current = true; setBusy(true); setFailed(false);
    try {
      let item = current.current;
      if (item) item = await clientCreationApi.inspect(item.apply_id);
      else {
        if (savedCreation(storageKey)) throw new Error('Pending creation must be recovered first');
        // Persist before the first mutation: its response may never arrive.
        sessionStorage.setItem(storageKey, expectedId);
        current.current = { apply_id: expectedId, plan_digest: plan.plan_digest, status: 'UNKNOWN' };
        item = await clientCreationApi.confirm(draft, plan);
      }
      if (!active()) return;
      if (item.plan_digest !== plan.plan_digest) throw new Error('Receipt mismatch');
      current.current = item;

      if (!['APPLIED', 'VERIFIED'].includes(item.status)) item = await clientCreationApi.apply(item);
      if (!active()) return;
      if (!['APPLIED', 'VERIFIED'].includes(item.status)) throw new Error('Creation incomplete');
      item = await clientCreationApi.verify(item);
      if (!active()) return;
      if (item.status !== 'VERIFIED') throw new Error('Verification incomplete');
      current.current = item;
      try { if (user) sessionStorage.removeItem(receiptStorageKey(user)); } catch { /* Server remains authoritative. */ }
      setDone(true);
    } catch {
      if (active()) setFailed(true);
    } finally {
      inFlight.current = false;
      if (active()) setBusy(false);
    }
  };

  if (pendingId && (pendingId !== expectedId || failed)) return <ResumeClientCreation applyId={pendingId} storageKey={storageKey} initialFailure={failed} />;
  if (capabilityFailed) return <section role="alert"><p>{t('creation.failed')}</p>
    <button type="button" onClick={() => setCapabilityAttempt(n => n + 1)}>{t('creation.retry')}</button></section>;
  if (done) return <section role="status" className="rounded-xl border border-emerald-300 bg-emerald-50 p-4 space-y-3">
    <h3 className="font-semibold">{t('creation.success')}</h3>
    <p>{t('creation.nextStep')}</p>
    <a href="/admin/clients" className="inline-block min-h-[44px] underline">{t('creation.openClients')}</a>
  </section>;
  return <section className="rounded-xl border border-[var(--border-light)] p-4 space-y-3" data-testid="client-creation">
    <label className="flex items-start gap-3"><input type="checkbox" checked={accepted} disabled={busy || !enabled}
      onChange={event => setAccepted(event.target.checked)} className="mt-1" />
      <span>{t('creation.confirm', { name: draft.client.display_name, email: draft.primary_user.email })}</span></label>
    {!enabled && <p role="status">{t('creation.unavailable')}</p>}
    {failed && <p role="alert">{t('creation.failed')}</p>}
    <button type="button" onClick={create} disabled={busy || !accepted || !enabled}
      className="min-h-[44px] px-4 py-2 rounded-xl bg-[var(--color-primary)] text-white disabled:opacity-50">
      {busy ? t('creation.busy') : failed ? t('creation.retry') : t('creation.create')}
    </button>
  </section>;
}

export default function ClientCreation(props: Props) {
  const { user } = useAuth();
  return user?.role === 'superadmin'
    ? <Creation key={JSON.stringify([user.id, user.org_id, props.plan.plan_digest])} {...props} /> : null;
}
